<template>
  <b-form id="edit-app-form" @submit.prevent="submitForm">
    <b-form-group>
      <template v-slot:label>{{ $t("appName") }} <span class="text-danger">*</span></template>
      <b-form-input type="text" v-model="name" required minlength="2" maxlength="100" />
    </b-form-group>
    <b-form-group>
      <template v-slot:label>Domain <span class="text-danger">*</span></template>
      <b-form-input type="text" v-model="domainName" required minlength="3" maxlength="200" />
    </b-form-group>
    <b-form-group>
      <template v-slot:label>Logo <span class="text-danger">*</span></template>
      <div class="d-flex flex-row align-items-center mb-2">
        <div v-if="logo" class="logo-container bg-primary mr-3">
          <img :src="logo" alt="logo">
        </div>
        <div class="min-width-0 overflow-hidden">
          <template v-if="logoFile">
            <div class="overflow-hidden text-nowrap text-overflow-ellipsis">{{ logoFile.name }}</div>
            <div>
              <span class="text-primary cursor-pointer" @click="cancelFileInput">{{ $t("cancel") }}</span>
            </div>
          </template>
          <span v-else class="text-primary cursor-pointer" @click="onClickSelectFile">{{ $t("change") }}</span>
          <b-form-file ref="logoInput" v-model="logoFile" @input="onFileInput" accept="image/*" plain hidden />
        </div>
      </div>
    </b-form-group>

    <b-form-group :label="$t('customer')" :hidden="selectedCustomer">
      <v-select class="vs-normalizer" :clearable="false" :filterable="false"
                :options="customerTypeaheadOptions"
                @search="doSearchCustomerDebounced" @option:selected="onSelectCustomer">
        <template v-slot:search="{attributes, events}">
          <input id="input-name" v-bind="attributes" v-on="events" class="form-control" :placeholder="$t('customerAutofillPlaceholder')"/>
        </template>
      </v-select>
    </b-form-group>
    <b-form-group :label="$t('customer')" v-if="selectedCustomer">
      <b-input-group>
        <b-form-input type="text" readonly v-model="selectedCustomer.label" />
        <b-input-group-append>
          <b-input-group-text class="cursor-pointer" @click="selectedCustomer = null">
            <b-icon-x />
          </b-input-group-text>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>

    <div class="text-right pt-3">
      <b-button type="button" variant="secondary" class="mr-3" @click="onClickCancel" :disabled="isSubmitting">{{ $t("cancel") }}</b-button>
      <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("updateApp") }}</b-button>
    </div>

  </b-form>
</template>

<style lang="scss" scoped>
  #edit-app-form {
    .logo-container {
      flex-shrink: 0;
      width: 50px; height: 50px;
      display: grid; place-items: center;

      > img {
        width: 28px;
      }
    }
  }
</style>

<script lang="ts">
  import {Component, Emit, Prop, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";

  import * as customerService from "@/service/customerService";
  import * as appNamesService from "@/service/appNamesService";
  import * as util from "@/service/util";
  import {BFormFile} from "bootstrap-vue";


  @Component
  export default class EditAppModal extends BaseComponent {

    @Prop({ required: true }) modalId: string;
    @Prop({ required: true }) item: any;

    name = "";
    domainName = "";

    logo = "";
    logoFile: File = null;

    selectedCustomer = null;
    customerTypeaheadOptions = [];
    doSearchCustomerDebounced = _.noop;

    formErrorMsg = "";
    isSubmitting = false;

    @Ref("logoInput") logoInput: BFormFile;

    // Emit decorators only
    @Emit("submitted") __submitted() {}


    mounted() {
      this.name = this.item.name;
      this.domainName = this.item.domainName;
      this.logo = this.item.logo;

      if (this.item.customerId) {
        this.selectedCustomer = {
          id: this.item.customerId,
          name: this.item.customerName,
          label: this.item.customerName
        };
      }

      this.doSearchCustomerDebounced = _.debounce(async (q: string, loading: (p: boolean) => void) => {
        try {
          if (q.length < 2) {
            this.customerTypeaheadOptions = [];
          } else {
            loading(true);
            this.customerTypeaheadOptions = (await customerService.getCustomers({ q, page: 1, pageSize: 10 })).data;
            for (const customer of this.customerTypeaheadOptions) {
              const { customerCode, name, email, phoneNumber } = customer;
              customer.label = util.joinStrings([customerCode, name, email, phoneNumber], " - ");
            }
          }

        } catch (e) {
          this.toastError(e);

        } finally {
          loading(false);
        }
      }, 300);
    }

    onSelectCustomer(customer: any) {
      this.selectedCustomer = customer;
    }

    onClickSelectFile() {
      // @ts-ignore
      this.logoInput.$el.click();
    }

    async onFileInput(file: File) {
      this.logo = file? await util.fileToBase64(file) : this.item.logo;
    }

    cancelFileInput() {
      this.logo = this.item.logo;
      this.logoFile = null;
    }

    onClickCancel() {
      this.$bvModal.hide(this.modalId);
    }

    async submitForm() {
      this.formErrorMsg = "";

      try {
        this.isSubmitting = true;

        const data = {
          id: this.item.id,
          name: this.name,
          domainName: this.domainName,
          logo: this.logo,
          logoExt: this.item.logoExt,
          active: true,
          customerId: this.selectedCustomer?.id,
          customerName: this.selectedCustomer?.name
        };
        if (this.logoFile) {
          data.logoExt = this.logoFile.name.slice(this.logoFile.name.lastIndexOf(".")).replace(".", "");
        }
        await appNamesService.update(data);

        this.__submitted();
        this.toastSuccessDelay(this.$t("updateAppSuccessful").toString());
        this.$bvModal.hide(this.modalId);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isSubmitting = false;
      }
    }

  }
</script>
